import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import { useEffect } from 'react';

import BackgroundBlobs from '../components/_molecules/BackgroundBlobs/BackgroundBlobs';
import useSignIn from '../hooks/useSignIn';
import { detectAndBestEffortSetLanguage } from '../utils/localisationUtils';
import { isSafeRedirect } from '../utils/navigator';
import { readPostAuthRedirect, savePostAuthRedirect } from '../utils/postAuthRedirect';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../utils/react-query-utils';
import { provisionFlags } from '../utils/ServerSideFeatureFlagUtils';

const Login = () => {
  const { signIn } = useSignIn();
  const session = useSession();
  const router = useRouter();
  const sessionStatus = session.status;
  const redirectParam = router.query.redirect;
  useEffect(() => {
    const fromQuery = isSafeRedirect(redirectParam) ? redirectParam : null;
    if (fromQuery) savePostAuthRedirect(fromQuery);
    const effectiveRedirect = fromQuery ?? readPostAuthRedirect() ?? '/';
    if (session.status === 'unauthenticated') {
      signIn({ callbackUrl: effectiveRedirect });
    }
    if (session.status === 'authenticated') {
      router.push(effectiveRedirect);
      return;
    }
  }, [router, session, sessionStatus, signIn, redirectParam]);
  return (
    <>
      <BackgroundBlobs />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'no-store, no-cache, must-revalidate, proxy-revalidate',
  );
  const session = await getSession({ req: context.req });
  const redirectParam = context.query.redirect;
  const redirect = isSafeRedirect(redirectParam) ? redirectParam : '/';

  if (session) {
    // If user is already authenticated, redirect to the requested URL (or home)
    return {
      redirect: {
        permanent: false,
        destination: redirect,
      },
    };
  } else {
    // If user is not authenticated, show the login page with all necessary props
    const queryClient = new QueryClient();
    const flags = await provisionFlags(context, queryClient, session);
    //if there is no access to the flag, redirect to the lessons page
    return {
      props: {
        ...flags,
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'app' })),
        session,
      },
    };
  }
};

export default Login;
